import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import api from "../api/axios";
import { State, City } from "country-state-city";

const INDIA_CODE = "IN";

export default function NewProject() {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    department: "",
    projectName: "",
    state: "",
    district: "",
    customerType: "",
    customerName: "",
    pocName: "",
    pocWhatsapp: "",
    bdName: "",
    workOrderNumber: "",
    workOrderDate: "",
    contractPeriod: "",
    qty: "",
    unit: "",
  });

  /* Load Indian states on mount */
  useEffect(() => {
    const indianStates = State.getStatesOfCountry(INDIA_CODE);
    setStates(indianStates);

    try {
      // Preload departments from API later
      setLoading(true);
      const fetchDepartments = async () => {
        const { data: response } = await api.get("/departments/list");
        setDepartments(response.data);
      };
      fetchDepartments();
    } catch (err) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Unable to fetch departments. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // When state changes → reload districts
    if (name === "state") {
      setForm((prev) => ({
        ...prev,
        state: value,
        district: "",
      }));

      const selectedState = states.find((s) => s.isoCode === value);
      if (selectedState) {
        const cities = City.getCitiesOfState(INDIA_CODE, selectedState.isoCode);
        setDistricts(cities);
      }
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post("/projects/create", form);

      if (response.data.success) {
        console.log("Project created:", response.data.project);

        // Optional: show success message or redirect
        alert("Project created successfully");

        // Optional: reset form
        setForm({
          department: "",
          projectName: "",
          state: "",
          district: "",
          customerType: "",
          customerName: "",
          pocName: "",
          pocWhatsapp: "",
          bdName: "",
          workOrderNumber: "",
          workOrderDate: "",
          contractPeriod: "",
          qty: "",
          unit: "",
        });
      } else {
        alert(response.data.message || "Failed to create project");
      }
    } catch (error) {
      console.error("Create project error:", error);

      alert(
        error.response?.data?.message ||
          "Unable to create project. Please try again.",
      );
    }
  };

  return (
    <Box
      p={3}
      maxWidth={700}
      mx="auto"
      bgcolor="background.paper"
      boxShadow={3}
    >
      <Typography variant="h5" gutterBottom>
        Add New Project
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container direction="column" spacing={3}>
          {/* Lead Department */}
          <Grid>
            <TextField
              select
              label="Lead Department"
              name="department"
              value={form.department}
              onChange={handleChange}
              fullWidth
              required
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.dept_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Project Name */}
          <Grid>
            <TextField
              label="Project Name"
              name="projectName"
              value={form.projectName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* State + District */}
          <Grid>
            <TextField
              select
              label="Project State"
              name="state"
              value={form.state}
              onChange={handleChange}
              fullWidth
              required
            >
              {states.map((state) => (
                <MenuItem key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Project District / Town"
              name="district"
              value={form.district}
              onChange={handleChange}
              fullWidth
              required
              disabled={!form.state}
            >
              {districts.map((city) => (
                <MenuItem key={city.name} value={city.name}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Customer Type - Govt. / Private */}
          <Grid>
            <TextField
              select
              label="Customer Type"
              name="customerType"
              value={form.customerType}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="1">Private</MenuItem>
              <MenuItem value="0">Government</MenuItem>
            </TextField>
          </Grid>

          {/* Customer Name */}
          <Grid>
            <TextField
              label="Customer Name"
              name="customerName"
              value={form.customerName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* POC Name */}
          <Grid>
            <TextField
              label="Customer's POC Name"
              name="pocName"
              value={form.pocName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* POC WhatsApp Number */}
          <Grid>
            <TextField
              label="POC WhatsApp Number"
              name="pocWhatsapp"
              value={form.pocWhatsapp}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* SPPL BD Name */}
          <Grid>
            <TextField
              label="SPPL BD Name"
              name="bdName"
              value={form.bdName}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Work Order Number */}
          <Grid>
            <TextField
              label="Work Order Number"
              name="workOrderNumber"
              value={form.workOrderNumber}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Work Order Date */}
          <Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Work Order Date"
                value={form.workOrderDate ? dayjs(form.workOrderDate) : null}
                onChange={(newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    workOrderDate: newValue
                      ? newValue.format("YYYY-MM-DD")
                      : "",
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Contract Period */}
          <Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Contract Period (Deadline)"
                value={form.contractPeriod ? dayjs(form.contractPeriod) : null}
                onChange={(newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    contractPeriod: newValue
                      ? newValue.format("YYYY-MM-DD")
                      : "",
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Quantity of Work */}
          <Grid>
            <TextField
              label="Billing Quantity of Work"
              name="qty"
              value={form.qty}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Billing Unit */}
          <Grid>
            <TextField
              select
              label="Billing Unit"
              name="unit"
              value={form.unit}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="nos">Nos</MenuItem>
              <MenuItem value="m">Meters</MenuItem>
              <MenuItem value="km">Kilometers</MenuItem>
              <MenuItem value="ha">Hectare</MenuItem>
              <MenuItem value="ac">Acre</MenuItem>
              <MenuItem value="sqkm">Square Kilometers</MenuItem>
            </TextField>
          </Grid>

          {/* Submit */}
          <Grid>
            <Button type="submit" variant="contained" size="large" fullWidth>
              Create Project
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
